/**
 * User and permission schemas live here so `ContactSchema` can import `UserSchema`
 * without a circular dependency on `FirmSchema` (which imports `ContactSchema`).
 */
import {
  EmailSchema,
  PhoneNumberSchema,
} from "@/lib/data/schemas/CommonSchemaFields";
import { commonFirmFields } from "@/lib/data/schemas/firm/FirmCommonFields";
import { NOTIFICATION_DELIVERY_OPTIONS } from "@/lib/data/static/notificationDeliveryStatics";
import { z } from "zod";

/** API often returns `null` for empty optionals. */
const nullishBoolean = z.boolean().nullish();
const nullishString = z.string().nullish();

export const PermissionValueSchema = z.enum([
  "view",
  "create",
  "update",
  "userMFAUpdate",
  "userGroupAssigning",
  "delete",
  "documentEdit",
]);

export const PermissionGroupActionSchema = z.enum([
  "view",
  "create",
  "update",
  "delete",
  "cancel",
  "accept",
  "decline",
  "notify.low",
  "notify.high",
]);

export const PermissionGrantSchema = z.object({
  permissionId: z.string().min(1),
  action: PermissionGroupActionSchema,
});

export const PermissionGroupSchema = z.object({
  id: z.string(),
  firmId: z.string(),
  label: z.string().min(1),
  description: z.string().nullable(),
  grants: z.array(PermissionGrantSchema),
  dateCreated: z.string(),
  dateModified: z.string(),
});

export const PermissionCatalogDefinitionSchema = z.object({
  id: z.string(),
  kind: z.enum(["page", "object"]),
  label: z.string(),
  displayOrder: z.number(),
  actions: z.array(
    z.object({
      action: PermissionGroupActionSchema,
      displayOrder: z.number(),
    }),
  ),
  parentPermissionIds: z.array(z.string()),
});

export const UserGroupPermissionsSchema = z.object({
  // Settings
  settingsAccess: z.boolean(),
  settingsContactRelationshipPermissions: z.array(PermissionValueSchema),
  settingsDefaultMatterExpensePermissions: z.array(PermissionValueSchema),
  settingsDefaultMatterFileRepositoryStructurePermissions: z.array(
    PermissionValueSchema,
  ),
  settingsFirmDetailPermissions: z.array(PermissionValueSchema),
  settingsLeadSourcePermissions: z.array(PermissionValueSchema),
  settingsFirmOfficePermissions: z.array(PermissionValueSchema),
  settingsFirmPhonePermissions: z.array(PermissionValueSchema),
  settingsMatterIntakeTemplatePermissions: z.array(PermissionValueSchema),
  settingsMatterRolePermissions: z.array(PermissionValueSchema),
  settingsMatterStatusPermissions: z.array(PermissionValueSchema),
  settingsMatterScheduledMessagePermissions: z.array(PermissionValueSchema),
  settingsMatterLitigationDatePlanPermissions: z.array(PermissionValueSchema),
  settingsMatterTaskPlanPermissions: z.array(PermissionValueSchema),
  settingsMatterTeamPermissions: z.array(PermissionValueSchema),
  settingsMatterTemplatePermissions: z.array(PermissionValueSchema),
  settingsMatterTypePermissions: z.array(PermissionValueSchema).optional(),
  settingsTagPermissions: z.array(PermissionValueSchema),
  settingsUserGroupPermissions: z.array(PermissionValueSchema),
  settingsUserPermissions: z.array(PermissionValueSchema),
  // Users
  usersCommunicationPermissions: z.array(PermissionValueSchema),
  /**
   * Optional guards exist so TypeScript recognizes that some of these values
   * might not exist in the user provider — they should always exist, but if a
   * new permission is added it might not be present yet, and the page will
   * crash without a guard clause when reading legacy user-group permissions.
   */

  // Contacts
  contactsAccess: z.boolean(),
  contactsDetailPermissions: z.array(PermissionValueSchema),
  contactsAddressPermissions: z.array(PermissionValueSchema),
  contactsConnectionPermissions: z.array(PermissionValueSchema),
  contactsCommunicationPermissions: z.array(PermissionValueSchema),
  contactsEmailPermissions: z.array(PermissionValueSchema),
  contactsFaxPermissions: z.array(PermissionValueSchema),
  contactsNotePermissions: z.array(PermissionValueSchema),
  contactsPhonePermissions: z.array(PermissionValueSchema),
  contactsTagConnectionPermissions: z.array(PermissionValueSchema),
  contactsWebsitePermissions: z.array(PermissionValueSchema),

  // Matters
  mattersAccess: z.boolean(),
  mattersAssigneePermissions: z.array(PermissionValueSchema),
  mattersContactConnectionPermissions: z.array(PermissionValueSchema),
  mattersDetailPermissions: z.array(
    z.union([
      PermissionValueSchema,
      z.literal("confirmStatuteOfLimitations"),
      z.literal("updateMatterStatus"),
    ]),
  ),
  mattersEmailPermissions: z.array(PermissionValueSchema),
  mattersExpensePermissions: z.array(PermissionValueSchema),
  mattersFilePermissions: z.array(PermissionValueSchema),
  mattersShareFolderPermissions: z.array(PermissionValueSchema),
  mattersHealthInsurancePermissions: z.array(PermissionValueSchema).optional(),
  mattersIncidentReportPermissions: z.array(PermissionValueSchema),
  mattersInsurancePermissions: z.array(PermissionValueSchema),
  mattersIntakeTemplateResponsePermissions: z.array(PermissionValueSchema),
  mattersLienPermissions: z.array(PermissionValueSchema),
  mattersLitigationDatePermissions: z.array(PermissionValueSchema),
  mattersLostWagesPermissions: z.array(PermissionValueSchema),
  mattersMedicalTreatmentPermissions: z.array(PermissionValueSchema),
  mattersNotePermissions: z.array(PermissionValueSchema),
  mattersRelatedMattersPermissions: z.array(PermissionValueSchema),
  mattersSettlementPermissions: z.array(PermissionValueSchema),
  mattersSignatureRequestPermissions: z.array(PermissionValueSchema),
  mattersStatuteOfLimitationsPermissions: z.array(PermissionValueSchema),
  mattersPropertyDamagePermissions: z.array(PermissionValueSchema),
  mattersTagConnectionPermissions: z.array(PermissionValueSchema),
  mattersTaskPermissions: z.array(
    z.union([
      PermissionValueSchema,
      z.literal("taskCommentUpdate"),
      z.literal("taskCommentDelete"),
    ]),
  ),
  mattersTrustCheckPermissions: z.array(PermissionValueSchema),
  mattersUserConnectionPermissions: z.array(PermissionValueSchema),
  mattersVehiclePermissions: z.array(PermissionValueSchema),
  // Mass torts
  massTortsAccess: z.boolean().optional(),
  massTortsCustomFieldPermissions: z.array(PermissionValueSchema).optional(),
  massTortsDetailPermissions: z.array(PermissionValueSchema).optional(),
  // Firm Files
  firmFilePermissions: z.array(PermissionValueSchema).optional(),
  // Referral partners
  referralPartnerAccess: z.boolean().optional(),
  referralPartnerPermissions: z.array(PermissionValueSchema).optional(),
  // Reports
  reportsAccess: z.boolean().optional(),
  reportsMatterActivityPermissions: z.array(PermissionValueSchema).optional(),
  reportsMatterDemandReportPermissions: z
    .array(PermissionValueSchema)
    .optional(),
  reportsMatterRecordRequestsReportPermissions: z
    .array(PermissionValueSchema)
    .optional(),
  reportsMattersApproachingStatuteOfLimitationPermissions: z
    .array(PermissionValueSchema)
    .optional(),
  reportsMatterStatusCountPermissions: z
    .array(PermissionValueSchema)
    .optional(),
  reportsMatterSettlementReportPermissions: z
    .array(PermissionValueSchema)
    .optional(),
  reportsOpenMattersReportPermissions: z
    .array(PermissionValueSchema)
    .optional(),
  reportsUserProductivityReportPermissions: z
    .array(PermissionValueSchema)
    .optional(),
});

export const UserNotificationPreferencesSchema = z.object({
  notificationMattersAssignee: nullishBoolean,
  notificationMattersAssigneeDelivery: z
    .array(z.enum(NOTIFICATION_DELIVERY_OPTIONS))
    .nullish(),
  notificationMattersContactConnection: nullishBoolean,
  notificationMattersContactConnectionDelivery: z
    .array(z.enum(NOTIFICATION_DELIVERY_OPTIONS))
    .nullish(),
  notificationMattersDetail: nullishBoolean,
  notificationMattersDetailDelivery: z
    .array(z.enum(NOTIFICATION_DELIVERY_OPTIONS))
    .nullish(),
  notificationMattersExpense: nullishBoolean,
  notificationMattersExpenseDelivery: z
    .array(z.enum(NOTIFICATION_DELIVERY_OPTIONS))
    .nullish(),
  notificationMattersFile: nullishBoolean,
  notificationMattersFileDelivery: z
    .array(z.enum(NOTIFICATION_DELIVERY_OPTIONS))
    .nullish(),
  notificationMattersIncidentReport: nullishBoolean,
  notificationMattersIncidentReportDelivery: z
    .array(z.enum(NOTIFICATION_DELIVERY_OPTIONS))
    .nullish(),
  notificationMattersInsurance: nullishBoolean,
  notificationMattersInsuranceDelivery: z
    .array(z.enum(NOTIFICATION_DELIVERY_OPTIONS))
    .nullish(),
  notificationMattersLien: nullishBoolean,
  notificationMattersLienDelivery: z
    .array(z.enum(NOTIFICATION_DELIVERY_OPTIONS))
    .nullish(),
  notificationMattersLitigationDate: nullishBoolean,
  notificationMattersLitigationDateDelivery: z
    .array(z.enum(NOTIFICATION_DELIVERY_OPTIONS))
    .nullish(),
  notificationMattersLostWages: nullishBoolean,
  notificationMattersLostWagesDelivery: z
    .array(z.enum(NOTIFICATION_DELIVERY_OPTIONS))
    .nullish(),
  notificationMattersMedicalTreatment: nullishBoolean,
  notificationMattersMedicalTreatmentDelivery: z
    .array(z.enum(NOTIFICATION_DELIVERY_OPTIONS))
    .nullish(),
  notificationMattersNote: nullishBoolean,
  notificationMattersNoteComment: nullishBoolean,
  notificationMattersNoteDelivery: z
    .array(z.enum(NOTIFICATION_DELIVERY_OPTIONS))
    .nullish(),
  notificationMattersPropertyDamage: nullishBoolean,
  notificationMattersPropertyDamageDelivery: z
    .array(z.enum(NOTIFICATION_DELIVERY_OPTIONS))
    .nullish(),
  notificationMattersSettlement: nullishBoolean,
  notificationMattersSettlementDelivery: z
    .array(z.enum(NOTIFICATION_DELIVERY_OPTIONS))
    .nullish(),
  notificationMattersSignatureRequest: nullishBoolean,
  notificationMattersSignatureRequestDelivery: z
    .array(z.enum(NOTIFICATION_DELIVERY_OPTIONS))
    .nullish(),
  notificationMattersTagConnection: nullishBoolean,
  notificationMattersTagConnectionDelivery: z
    .array(z.enum(NOTIFICATION_DELIVERY_OPTIONS))
    .nullish(),
  notificationMattersTask: nullishBoolean,
  notificationMattersTaskComment: nullishBoolean,
  notificationMattersTaskDelivery: z
    .array(z.enum(NOTIFICATION_DELIVERY_OPTIONS))
    .nullish(),
  notificationMattersTrustCheck: nullishBoolean,
  notificationMattersTrustCheckDelivery: z
    .array(z.enum(NOTIFICATION_DELIVERY_OPTIONS))
    .nullish(),
  notificationMattersUserConnection: nullishBoolean,
  notificationMattersUserConnectionDelivery: z
    .array(z.enum(NOTIFICATION_DELIVERY_OPTIONS))
    .nullish(),
  notificationMattersVehicle: nullishBoolean,
  notificationMattersVehicleDelivery: z
    .array(z.enum(NOTIFICATION_DELIVERY_OPTIONS))
    .nullish(),
});

/** Nested `firm` object on the current-user payload (uses `id`, not `firmId`). */
export const UserFirmSchema = z.object({
  id: z.string(),
  name: z.string(),
  description: nullishString,
  colorTheme: z.enum(["default", "green", "orange", "purple"]).nullish(),
  onboardingProgress: nullishString,
  profilePicturePresignedUrlKey: nullishString,
  dateCreated: z.string(),
  dateModified: z.string(),
});

/** Nested `userGroup` object on the current-user payload. */
export const UserUserGroupSchema = z
  .object({
    id: z.string().min(1, { message: "Must select a user group" }),
    description: z
      .string()
      .max(300, { message: "Description is too long" })
      .nullish()
      .or(z.literal("")),
    label: z.string().min(1, { message: "Label is required" }).max(50, {
      message: "Label is too long",
    }),
  })
  .merge(commonFirmFields)
  .merge(UserGroupPermissionsSchema);

export const UserSchema = z
  .object({
    id: z.string(),
    firstName: z
      .string()
      .trim()
      .min(1, { message: "First name is required" })
      .max(100, {
        message: "First name is too long",
      }),
    middleName: z
      .string()
      .trim()
      .max(100, {
        message: "Middle name is too long",
      })
      .nullish(),
    lastName: z
      .string()
      .trim()
      .min(1, { message: "Last name is required" })
      .max(100, {
        message: "Last name is too long",
      }),
    enabled: nullishBoolean,
    barNumber: z
      .string()
      .max(100, { message: "Bar number must be 100 characters or less" })
      .nullish(),
    owner: nullishBoolean,
    email: EmailSchema,
    phoneNumber: PhoneNumberSchema,
    profilePicturePresignedUrlKey: nullishString,
    userGroupId: nullishString,
    permissionGroupIds: z.array(z.string()),
    defaultIntakeMethod: z.enum(["role", "team"]).nullish(),
    defaultIntakeMethodId: nullishString,
    dashboardNotifications: nullishBoolean,
    dashboardTasks: nullishBoolean,
    dashboardMyApproachingStatuteOfLimitationMatters: nullishBoolean,
    dashboardMyMatters: nullishBoolean,
    dashboardFavoriteMatters: nullishBoolean,
    dashboardMostRecentMatters: nullishBoolean,
    cognitoStatus: nullishString,
    status: nullishString,
    username: nullishString,
    firm: UserFirmSchema,
    userGroup: UserUserGroupSchema.nullish(),
    permissionGroups: z.array(PermissionGroupSchema),
    effectiveGrants: z.array(PermissionGrantSchema),
  })
  .merge(commonFirmFields)
  .merge(UserNotificationPreferencesSchema);

/** Nested user payload embedded on notes, comments, assignees, and connections. */
export const EmbeddedUserSchema = UserSchema.pick({
  id: true,
  firstName: true,
  middleName: true,
  lastName: true,
  email: true,
  phoneNumber: true,
  owner: true,
  enabled: true,
  barNumber: true,
  profilePicturePresignedUrlKey: true,
  userGroupId: true,
}).partial();
